/**
 * Display mode context hooks and utilities
 */

import React, { useContext } from 'react';
import { DisplayModeContext } from '../contexts/DisplayModeContext';
import type { DisplayModeConfig } from '../types/displayMode';

/**
 * Hook to access display mode context
 * Throws if used outside of DisplayModeProvider
 */
export function useDisplayModeContext(): DisplayModeConfig {
  const context = useContext(DisplayModeContext);

  if (!context) {
    throw new Error(
      'useDisplayModeContext must be used within DisplayModeProvider'
    );
  }

  return context;
}

/**
 * Higher-order component to inject display mode props
 * Useful for class components or prop injection
 */
export function withDisplayMode<P extends object>(
  Component: React.ComponentType<P & DisplayModeConfig>
): React.ComponentType<P> {
  return function DisplayModeWrappedComponent(props: P) {
    const displayModeConfig = useDisplayModeContext();

    return <Component {...props} {...displayModeConfig} />;
  };
}
